import React, { useCallback, useEffect, useState } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';

import ResourceDetails from './ResourceDetails';
import { AD_ENTITY_TYPES, ENTITY_SELECTOR_FILTER_TYPES } from '@silverfort-nodelibs/common/utils/consts';

import SelectedEntitiesContainer from '../common/SelectedEntitiesContainer';
import SearchContainer from '../common/SearchContainer';

import { ENTITY_TYPES_TO_ICONS, SELECTOR_TYPE } from '../common/selectorsConsts';
import styles from './ADServersSelector.module.css';
import { allComputersResource, unresolvedResources } from '../../../Policy/common';

const ADServersSelector = ({
    modalTitle,
    selectedEntities,
    showModal,
    handleCloseModal,
    saveEntities,
    showExcluded,
    GlobalServices,
    allComputersObject,
    unresolvedResourcesObject,
    isEditor = true,
    showServices = true,
    showUnresolvedResources = false
}) => {
    const [currentSelectedEntities, setCurrentSelectedEntities] = useState([...selectedEntities]);
    const [focusedResource, setFocusedResource] = useState({});
    const [applyAllMsg, setApplyAllMsg] = useState({ text: '', isExclude: false });

    const doAnyIncludedEntitiesExist = useCallback((selectedEntities) => {
        return selectedEntities.some((entity) => {
            if (entity.guid !== allComputersResource.guid && !entity.excluded) {
                return true;
            }
            return false;
        });
    }, []);

    const isAllComputersIncluded = useCallback((selectedEntities) => {
        return selectedEntities.some((entity) => {
            if (entity.guid === allComputersResource.guid) {
                return true;
            }
            return false;
        });
    }, []);

    useEffect(() => {
        if (applyAllMsg) {
            const timer = setTimeout(() => setApplyAllMsg({ text: '', isExclude: false }), 5000);
            return () => {
                clearTimeout(timer);
            };
        }
    }, [applyAllMsg]);

    // Add chips to lists
    useEffect(() => {
        if (!currentSelectedEntities) {
            return;
        }
        for (const entityIndex in currentSelectedEntities) {
            if (
                focusedResource.guid === currentSelectedEntities[entityIndex].guid &&
                focusedResource.selectedServices?.length !==
                currentSelectedEntities[entityIndex].selectedServices?.length
            ) {
                const newSelectedEntities = [...currentSelectedEntities];
                const updatedEntity = newSelectedEntities.splice(entityIndex, 1)[0];
                newSelectedEntities.push({
                    ...updatedEntity,
                    selectedServices: focusedResource.selectedServices ? [...focusedResource.selectedServices] : [],
                    match_all_services: focusedResource.match_all_services
                });
                setCurrentSelectedEntities(newSelectedEntities);
            }
            const selectedEntity = currentSelectedEntities[entityIndex];
            if (selectedEntity.type !== AD_ENTITY_TYPES.SERVER) {
                selectedEntity.services = [...GlobalServices];
            } else if (!selectedEntity.services) {
                selectedEntity.services = [];
            }
            if (selectedEntity.selectedServices) {
                for (const serviceData of selectedEntity.selectedServices) {
                    if (!selectedEntity.services.includes(serviceData.service_identifier)) {
                        selectedEntity.services.push(serviceData.service_identifier);
                    }
                }
            }
        }
    }, [currentSelectedEntities, focusedResource, GlobalServices]);

    const onRemoveSelected = (index) => {
        const newSelectedEntities = [...currentSelectedEntities];
        if (focusedResource.guid === newSelectedEntities[index].guid) {
            setFocusedResource({});
        }
        newSelectedEntities.splice(index, 1);

        const haveAllComputersResource = isAllComputersIncluded(newSelectedEntities);
        if(!haveAllComputersResource){
            const haveAnyIncludedEntitiesThatAreNotUnresolved = newSelectedEntities.some((entity) => entity.guid !== unresolvedResources.guid && !entity.excluded);
            if (!haveAnyIncludedEntitiesThatAreNotUnresolved) {
                newSelectedEntities.push(allComputersObject);
            }
        }

        setCurrentSelectedEntities(newSelectedEntities);
    };

    const onSelectSuggestion = (suggestion, excluded, event) => {
        event.stopPropagation();
        const newSelectedEntities = [...currentSelectedEntities];

        if(suggestion.guid === unresolvedResources.guid){
            suggestion = { ...unresolvedResourcesObject };
        }

        suggestion.name = suggestion.display_name ? suggestion.display_name : suggestion.name;
        suggestion.excluded = excluded;
        suggestion.is_protected = true;
        insertResourceServices(suggestion);
        newSelectedEntities.push(suggestion);
        const haveAnyIncludedEntities = doAnyIncludedEntitiesExist(newSelectedEntities);
        const haveOnlyUnresolvedResourcesSelected = newSelectedEntities.every((entity) => entity.guid === unresolvedResources.guid || entity.guid === allComputersResource.guid || entity.excluded);
        if (haveAnyIncludedEntities && !haveOnlyUnresolvedResourcesSelected) {
            const allComputersIndex = newSelectedEntities.findIndex(
                (entity) => entity.guid === allComputersResource.guid
            );
            if (allComputersIndex !== -1) {
                newSelectedEntities.splice(allComputersIndex, 1);
            }
        }
        setCurrentSelectedEntities(newSelectedEntities);
        onResourceClicked(suggestion);
    };

    const insertResourceServices = (resource) => {
        if (resource.type !== AD_ENTITY_TYPES.SERVER) {
            resource.services = [...GlobalServices];
            resource.customServices = [];
        }

        resource.selectedServices = [];
        resource.match_all_services = true;
    };

    const onResourceClicked = (resource) => {
        setFocusedResource(resource);
    };

    const closeSelector = () => {
        if (handleCloseModal) {
            handleCloseModal();
        }
    };

    const onSaveEntitiesClicked = () => {
        if (saveEntities) {
            saveEntities(currentSelectedEntities, showUnresolvedResources);
        }
    };

    const selectServicesToAllResources = (selectedServices, isExcluded, match_all_services) => {
        const selectedServicesNames = [];
        for (const selectedServiceName of selectedServices) {
            selectedServicesNames.push(selectedServiceName.service_identifier.split('/')[0]);
        }

        const newSelectedEntities = [];
        for (const entity of currentSelectedEntities) {
            const newEntity = { ...entity };
            if (entity.excluded === isExcluded) {
                newEntity.selectedServices = entity.services
                    .filter((service) => selectedServicesNames.includes(service.split('/')[0]))
                    .map((service) => {
                        return { service_identifier: service, type: 1 };
                    });
                newEntity.match_all_services = match_all_services;
            }

            newSelectedEntities.push(newEntity);
        }
        setCurrentSelectedEntities(newSelectedEntities);
        return true;
    };

    const haveAnyIncludedEntities = doAnyIncludedEntitiesExist(currentSelectedEntities);
    const haveAllComputersResource = isAllComputersIncluded(currentSelectedEntities);

    let servicesComponent = null
    if (showServices) {
        let innerComponent = null
        if (Object.keys(focusedResource).length === 0) {
            innerComponent = <div className={styles.noResource}>No Resource selected</div>
        } else if (haveAllComputersResource && focusedResource.excluded) {
            innerComponent = (<div className="text-center text-[#8d8f91] text-[25px] opacity-50">
                Can't select services while all resources is included
            </div>)
        } else if (focusedResource.guid === unresolvedResources.guid && haveAllComputersResource) {
            innerComponent = (
                <div className="text-center text-[#8d8f91] text-[25px] opacity-50">Can't select services for unresolved resources while all resources is included</div>
            )
        } else {
            innerComponent = (
                <ResourceDetails
                    focusedResource={focusedResource}
                    setFocusedResource={setFocusedResource}
                    selectServicesToAllResources={selectServicesToAllResources}
                    resourceIcon={ENTITY_TYPES_TO_ICONS[focusedResource.type]}
                    setApplyAllMsg={setApplyAllMsg}
                    isEditor={isEditor}
                />)
        }
        servicesComponent = (
            <div className={styles.detailsContainer}>
                {innerComponent}
            </div>
        )
    }

    return (
        <div className={styles.modalContainer}>
            <Modal
                id={styles.adServersSelectorModal}
                show={showModal}
                onHide={closeSelector}
                keyboard={true}
                className="z-[5000]"
                backdropClassName="z-[5000]"
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle ? modalTitle : 'Select Resources'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="flex flex-col">
                        <div className={styles.dataContainer}>
                            {isEditor && (
                                <div className="flex-grow">
                                    <SearchContainer
                                        selectorData={{
                                            filters: [
                                                ENTITY_SELECTOR_FILTER_TYPES.ALL,
                                                ENTITY_SELECTOR_FILTER_TYPES.SERVERS_ONLY,
                                                ENTITY_SELECTOR_FILTER_TYPES.GROUPS_ONLY,
                                                ENTITY_SELECTOR_FILTER_TYPES.OUS_ONLY
                                            ],
                                            placeholderText: 'Search servers, groups or organizational units',
                                            searchEntitiesTypes: [
                                                AD_ENTITY_TYPES.GROUP,
                                                AD_ENTITY_TYPES.OU,
                                                AD_ENTITY_TYPES.SERVER
                                            ]
                                        }}
                                        onSelectSuggestion={onSelectSuggestion}
                                        showExcluded={showExcluded}
                                        focusedResource={focusedResource}
                                        currentSelectedEntities={currentSelectedEntities}
                                        searchTableClassName="min-h-[392px]"
                                        showUnresolvedResources={showUnresolvedResources}
                                    />
                                </div>
                            )}
                            {showServices && servicesComponent}
                        </div>
                        <SelectedEntitiesContainer
                            selectedEntities={currentSelectedEntities}
                            onRemoveSelected={onRemoveSelected}
                            selectorType={SELECTOR_TYPE.RESOURCE}
                            focusedResource={focusedResource}
                            onResourceClicked={onResourceClicked}
                            applyAllMsg={applyAllMsg}
                            showExcluded={showExcluded}
                            removeIncludedBlocked={!haveAnyIncludedEntities}
                            removeExcludedBlocked={false}
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className={styles.footerButtons}>
                        <Button onClick={closeSelector}>Cancel</Button>
                        <Button bsStyle="success" onClick={onSaveEntitiesClicked} disabled={!isEditor}>
                            Done
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ADServersSelector;
